// Reports on the run page (design §7, contract §5): the "Report" block
// (link to the task's report + which version(s) this run wrote) and a reader
// for the `report_write` / `report_attach` run events in the timeline.

import { Link } from '@tanstack/react-router'
import type { RunEvent } from '@goblic/shared'
import { Panel } from '../ui'
import { useTaskReport } from '../../hooks/useReports'

export interface ReportRunEventView {
  type: 'report_write' | 'report_attach'
  reportId: string
  version: number | null
  title: string | null
  message: string | null
  missingAssets: string[]
  name: string | null
  size: number | null
  deduplicated: boolean | null
}

/**
 * A `report_write` / `report_attach` event, whether its fields arrive flat
 * (shared `ReportRunEvent`) or under `payload` (raw stored event), else null.
 */
export function reportRunEventOf(
  event: RunEvent | { type: string }
): ReportRunEventView | null {
  const raw = event as unknown as Record<string, unknown>
  if (raw.type !== 'report_write' && raw.type !== 'report_attach') return null
  const f =
    raw.payload && typeof raw.payload === 'object'
      ? { ...raw, ...(raw.payload as Record<string, unknown>) }
      : raw
  const str = (v: unknown) => (typeof v === 'string' ? v : null)
  const num = (v: unknown) => (typeof v === 'number' ? v : null)
  return {
    type: raw.type,
    reportId: str(f.reportId) ?? '',
    version: num(f.version),
    title: str(f.title),
    message: str(f.message),
    missingAssets: Array.isArray(f.missingAssets)
      ? f.missingAssets.filter((x): x is string => typeof x === 'string')
      : [],
    name: str(f.name),
    size: num(f.size),
    deduplicated: typeof f.deduplicated === 'boolean' ? f.deduplicated : null,
  }
}

/** Timeline line for a report event. */
export function describeReportEvent(view: ReportRunEventView): {
  text: string
  detail?: string
  tone?: 'signal' | 'ok'
} {
  if (view.type === 'report_write') {
    return {
      text: `Wrote report version ${view.version ?? '?'}${view.title ? `: ${view.title}` : ''}`,
      detail:
        [
          view.message,
          view.missingAssets.length
            ? `missing attachments: ${view.missingAssets.join(', ')}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ') || undefined,
      tone: view.missingAssets.length ? 'signal' : 'ok',
    }
  }
  return {
    text: `Attached ${view.name ?? 'a file'} to the report`,
    detail:
      [
        view.size != null ? formatBytes(view.size) : null,
        view.deduplicated ? 'already stored' : null,
      ]
        .filter(Boolean)
        .join(' · ') || undefined,
  }
}

/**
 * The run page's "Report" block. Shows nothing when the task has no report
 * and this run didn't touch one.
 */
export function RunReportPanel({
  taskId,
  events,
  live,
}: {
  taskId: string
  events: RunEvent[]
  live: boolean
}) {
  const reportQuery = useTaskReport(taskId, {
    refetchInterval: live ? 15_000 : false,
  })
  const report = reportQuery.data?.report ?? null
  const views = events
    .map((e) => reportRunEventOf(e))
    .filter((v): v is ReportRunEventView => v != null)
  const written = views
    .filter((v) => v.type === 'report_write' && v.version != null)
    .map((v) => v.version as number)
  const attached = views.filter((v) => v.type === 'report_attach').length

  if (!report && views.length === 0) return null

  return (
    <Panel title="Report">
      <div className="space-y-2 text-base">
        {report ? (
          <p>
            <Link
              to="/tasks"
              search={{ taskId, tab: 'report' }}
              className="link font-medium"
            >
              {report.title}
            </Link>
            <span className="text-ink-3">
              {' '}
              · v{report.currentVersion}
            </span>
          </p>
        ) : null}
        <p className="text-sm text-ink-2">
          {written.length === 0
            ? 'This run didn’t write a version.'
            : written.length === 1
              ? `This run wrote version ${written[0]}.`
              : `This run wrote versions ${written.join(', ')}.`}
          {attached > 0
            ? ` ${attached} attachment${attached === 1 ? '' : 's'} added.`
            : ''}
        </p>
        {report ? (
          <p>
            <Link
              to="/tasks/$taskId/report"
              params={{ taskId }}
              className="link text-sm"
            >
              Open full screen
            </Link>
          </p>
        ) : null}
      </div>
    </Panel>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * "View report" next to a PR link (review/approval screens): renders only
 * when the task has a report.
 */
export function TaskReportLink({
  taskId,
  className = 'link font-medium',
}: {
  taskId: string
  className?: string
}) {
  const reportQuery = useTaskReport(taskId)
  if (!reportQuery.data?.report) return null
  return (
    <Link to="/tasks" search={{ taskId, tab: 'report' }} className={className}>
      View report
    </Link>
  )
}
